import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MoreHorizontal, UserRound, Mail, Trash2, CalendarClock, CalendarPlus } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { useToast } from '@/shared/hooks/useToast';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback';
import { useActiveOrganization } from '@/features/organizations/public';
import { MemberWorkScheduleDialog } from '@/features/work-schedules/public';
import { useDeleteMember } from '../hooks/useDeleteMember';
import { SetHireDateDialog } from './SetHireDateDialog';
import type { Member } from '../types';

interface MemberRowActionsProps {
  member: Member;
}

export function MemberRowActions({ member }: MemberRowActionsProps) {
  const { t } = useTranslation('members');
  const { toast } = useToast();
  const { confirm, isOpen, options, handleConfirm, handleCancel } = useConfirm();
  const deleteMember = useDeleteMember();
  const { activeOrganization } = useActiveOrganization();
  const canManage = activeOrganization?.role === 'owner' || activeOrganization?.role === 'admin';
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [hireDateDialogOpen, setHireDateDialogOpen] = useState(false);

  const handleCopyEmail = async () => {
    await navigator.clipboard.writeText(member.email);
    toast.success({
      title: t('toast.emailCopied'),
      description: t('toast.emailCopiedDesc', { email: member.email }),
    });
  };

  const handleDelete = useDebouncedCallback(async () => {
    const result = await confirm({
      title: t('confirm.delete'),
      description: t('confirm.deleteDesc', { name: member.displayName }),
      confirmLabel: t('actions.delete'),
      cancelLabel: t('confirm.cancel'),
      variant: 'destructive',
    });

    if (!result.confirmed) return;

    try {
      await deleteMember.mutateAsync(member.id);
      toast.success({
        title: t('toast.deleted'),
        description: t('toast.deletedDesc', { name: member.displayName }),
      });
    } catch {
      // global handler shows error toast
    }
  });

  return (
    <>
      <DropdownMenu>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                disabled={deleteMember.isPending}
                className="h-7 w-7 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100 transition-opacity"
                aria-label={t('actions.openMenu')}
                data-testid={`members-row-actions-${member.id}`}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{t('actions.openMenu')}</TooltipContent>
        </Tooltip>

        <DropdownMenuContent align="end">
          <DropdownMenuItem disabled>
            <UserRound className="w-4 h-4" />
            {t('actions.viewProfile')}
          </DropdownMenuItem>

          <DropdownMenuItem onClick={handleCopyEmail}>
            <Mail className="w-4 h-4" />
            {t('actions.copyEmail')}
          </DropdownMenuItem>

          {canManage && (
            <DropdownMenuItem
              onClick={() => setScheduleDialogOpen(true)}
              data-testid={`members-row-action-work-schedule-${member.userId}`}
            >
              <CalendarClock className="w-4 h-4" />
              {t('actions.workSchedule')}
            </DropdownMenuItem>
          )}

          {canManage && (
            <DropdownMenuItem
              onClick={() => setHireDateDialogOpen(true)}
              data-testid={`members-row-action-hire-date-${member.userId}`}
            >
              <CalendarPlus className="w-4 h-4" />
              {t('actions.setHireDate')}
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />
          <DropdownMenuItem
            disabled={deleteMember.isPending}
            onClick={handleDelete}
            className="text-destructive focus:text-destructive focus:bg-destructive/10"
          >
            <Trash2 className="w-4 h-4" />
            {t('actions.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {isOpen && options && (
        <ConfirmDialog
          open={isOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={options.title}
          description={options.description}
          confirmLabel={options.confirmLabel}
          cancelLabel={options.cancelLabel}
          variant={options.variant}
          onConfirm={handleConfirm}
        />
      )}

      {canManage && scheduleDialogOpen && (
        <MemberWorkScheduleDialog
          member={{ userId: member.userId, displayName: member.displayName }}
          open={scheduleDialogOpen}
          onOpenChange={setScheduleDialogOpen}
        />
      )}

      {canManage && hireDateDialogOpen && (
        <SetHireDateDialog
          member={{ userId: member.userId, displayName: member.displayName }}
          open={hireDateDialogOpen}
          onOpenChange={setHireDateDialogOpen}
        />
      )}
    </>
  );
}
